import React from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../common/Modal';
import Button from '../common/Button';
import { Mail, RefreshCw, AlertCircle } from 'lucide-react';
import { attendeeAPI } from '../../api/endpoints';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const DrillDownModal = ({ isOpen, onClose, eventId, fieldName, fieldValue, fieldLabel }) => {
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['attendees-drill-down', eventId, fieldName, fieldValue],
    queryFn: async () => {
      if (!eventId || !fieldName || !fieldValue) return null;
      // We pass paginate=0 to get all of them, or we could just get page 1.
      // Assuming we want a list to display. Let's fetch the first page for the preview.
      const response = await attendeeAPI.getAll(eventId, {
        custom_field_name: fieldName,
        custom_field_value: fieldValue,
        per_page: 100
      });
      return response.data;
    },
    enabled: isOpen && !!eventId && !!fieldName && !!fieldValue,
  });

  const attendees = data?.attendees?.data || [];
  const totalCount = data?.attendees?.total || 0;

  const handleEmailSegment = () => {
    // Navigate to campaigns page and pass the segment parameters in URL state
    navigate('/campaigns', { 
      state: { 
        createCampaign: true, 
        filterField: fieldName, 
        filterValue: fieldValue,
        eventId: eventId
      } 
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${fieldLabel}: ${fieldValue}`}
      size="xl"
    >
      <div className="flex flex-col h-full max-h-[70vh]">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100">
          <p className="text-gray-600">
            Found <span className="font-bold text-gray-900">{totalCount}</span> attendees matching this segment.
          </p>
          <Button
            onClick={handleEmailSegment}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
            disabled={totalCount === 0 || isLoading}
          >
            <Mail className="h-4 w-4" />
            Email this Segment
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-[300px]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <RefreshCw className="h-8 w-8 animate-spin mb-4 text-blue-500" />
              <p>Loading attendees...</p>
            </div>
          ) : isError ? (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <AlertCircle className="h-8 w-8 mb-4" />
              <p>Failed to load attendees. Please try again.</p>
            </div>
          ) : attendees.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <p>No attendees found for this segment.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Registered</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {attendees.map((attendee) => (
                    <tr key={attendee.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{attendee.full_name}</td>
                      <td className="px-4 py-3 text-gray-500">{attendee.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          attendee.check_in_status === 'checked_in' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {attendee.check_in_status === 'checked_in' ? 'Checked In' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">
                        {formatDistanceToNow(new Date(attendee.created_at), { addSuffix: true })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalCount > 100 && (
                <div className="text-center p-4 text-sm text-gray-500 border-t border-gray-100">
                  Showing first 100 attendees. Use the Export button on the main page for the full list.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DrillDownModal;
